import type { WbsItemDoc, WbsItemKind } from '@mct/shared';

/**
 * Guard logic for switching a WBS row's `kind` between `task` and
 * `phase` from the detail panel. The toggle is intentionally limited to
 * those two values — `milestone` rows hide the switch entirely.
 *
 * Hard rules (return a `KindChangeViolation`):
 *   - A nested row (`parentItemId !== null`) cannot become a phase.
 *     Phases are conceptually top-level buckets; allowing them under a
 *     parent would violate the "phase under phase / phase under task"
 *     invariant the Gantt and rollup code rely on.
 *   - A row whose direct children include a `phase` cannot become a
 *     phase itself (phase-under-phase is unsupported).
 *
 * Soft rule (returned by `getKindChangeWarning`):
 *   - Demoting a phase back to a task while children still exist is
 *     allowed but flagged so the UI can confirm.
 */
export type KindChangeViolation =
  | { code: 'nested_to_phase'; message: string }
  | { code: 'phase_under_phase'; message: string };

export interface KindChangeWarning {
  code: 'phase_to_task_with_children';
  message: string;
  childCount: number;
}

type ItemSlice = Pick<WbsItemDoc, 'itemId' | 'kind' | 'parentItemId'>;

export const NESTED_TO_PHASE_MESSAGE =
  'ネストされたアイテムは phase に変更できません';

/**
 * Validate a proposed kind change. Returns `null` when the change is
 * allowed (with or without a soft warning — see `getKindChangeWarning`),
 * or a `KindChangeViolation` describing the hard-rule violation.
 *
 * Same-kind transitions are treated as no-ops (allowed).
 */
export function validateKindChange(
  item: ItemSlice,
  nextKind: WbsItemKind,
  allItems: readonly ItemSlice[],
): KindChangeViolation | null {
  if (nextKind === item.kind) return null;

  if (nextKind === 'phase') {
    if (item.parentItemId !== null) {
      return {
        code: 'nested_to_phase',
        message: NESTED_TO_PHASE_MESSAGE,
      };
    }
    const phaseChild = allItems.find(
      (i) => i.parentItemId === item.itemId && i.kind === 'phase',
    );
    if (phaseChild) {
      return {
        code: 'phase_under_phase',
        message:
          '子要素に phase が含まれているため phase に変更できません（phase の下に phase は不可）',
      };
    }
  }

  return null;
}

/**
 * Soft warning for `phase → task` demotion when children exist. Caller
 * should surface this through a confirm dialog before writing.
 */
export function getKindChangeWarning(
  item: ItemSlice,
  nextKind: WbsItemKind,
  allItems: readonly ItemSlice[],
): KindChangeWarning | null {
  if (nextKind === item.kind) return null;
  if (item.kind === 'phase' && nextKind === 'task') {
    const childCount = allItems.filter(
      (i) => i.parentItemId === item.itemId,
    ).length;
    if (childCount > 0) {
      return {
        code: 'phase_to_task_with_children',
        message: `子要素 ${childCount} 件があります。task に戻すと階層構造として不自然になる可能性があります。続行しますか？`,
        childCount,
      };
    }
  }
  return null;
}
